/**
 * Tuning values ported from the 2008 C++ source (original-2008/source/Gravity/**).
 * Units: world units and ticks. Speeds are units/tick, accelerations units/tick^2,
 * angles radians, angular speeds radians/tick. Keep them per tick: that preserves
 * the original feel exactly (see CLAUDE.md §3).
 *
 * Coordinate convention: the sim is y-down (screen space). Legacy was y-up (OpenGL),
 * so legacy maps are flipped on load and rotation signs are mirrored; see TURN_* below.
 */

/** legacy: Game.h m_millis_per_tick */
export const TICK_MS = 15;

// --- Gravity (GravityStrategy.cpp) ---
export const GRAVITY_G = 10;
/** Gravity is skipped closer than this to a planet's centre. */
export const GRAVITY_MIN_DISTANCE = 15;

// --- Ship (Ship.cpp, Player.cpp/.h) ---
export const SHIP_RADIUS = 28;
export const SHIP_MASS = 50;
/** Thrust added to velocity per tick along the facing direction. */
export const SHIP_THRUST = 0.08;
/** Speed cap, applied only while thrusting (gravity can push beyond it). */
export const SHIP_MAX_SPEED = 8;
/** Rotation per tick. Legacy: left = +0.09 in y-up, i.e. counter-clockwise on screen. */
export const SHIP_TURN_RATE = 0.09;
/** Sign applied to SHIP_TURN_RATE in our y-down space: left turns counter-clockwise on screen. */
export const TURN_LEFT_SIGN = -1;
export const TURN_RIGHT_SIGN = 1;
/** Ships per player including the first (legacy m_shipsLeft). */
export const SHIP_LIVES = 10;
/** Ticks between a ship's destruction and its respawn being queued. legacy: Player.h */
export const RESPAWN_DELAY_TICKS = 35;

// --- Weapons (Ship.cpp, *Projectile.cpp, WorldObjectsManager.cpp, ExpirationStrategy.cpp) ---
/** Laser charges +1.0/tick and fires at 1.0, i.e. one laser per tick while held. */
export const LASER_COOLDOWN_TICKS = 1;
/** Missile charges +0.1f/tick (float32) and is clamped at 1.0 → ready after 10 ticks. */
export const MISSILE_COOLDOWN_TICKS = 10;
export const LASER_RADIUS = 7;
export const LASER_MASS = 0;
export const LASER_SPEED = 20;
export const LASER_LIFETIME_TICKS = 40;
export const MISSILE_RADIUS = 10;
export const MISSILE_MASS = 30;
export const MISSILE_SPEED = 12;
/** legacy m_missileExpire = 0, which never counts down to zero → missiles never expire. */
export const MISSILE_LIFETIME_TICKS: number | null = null;
/** Extra gap between the ship's and the projectile's edge at launch. */
export const LAUNCH_GAP = 10;

// --- Asteroids (AsteroidStrategy.cpp, Asteroid.cpp) ---
export const ASTEROID_RADIUS = 20;
export const ASTEROID_MASS = 50;
/** Countdown reset value; an asteroid is queued when it reaches 0 (effective period 201 ticks). */
export const ASTEROID_SPAWN_INTERVAL = 200;
export const ASTEROID_MAX_SPEED = 6;
/** Launch angle drawn from [0, 3.14) in legacy y-up space (mirrored for y-down). */
export const ASTEROID_MAX_ANGLE = 3.14;
/** Spin drawn from [-0.025, 0.025) rad/tick. */
export const ASTEROID_MAX_SPIN = 0.025;

// --- Spawn points (SpawnPoint.cpp) ---
export const SPAWN_POINT_RADIUS = 32;
/** Random spawn points (rules.spawnPoints) keep this far from planet surfaces. */
export const RANDOM_SPAWN_PLANET_CLEARANCE = 250;
/** Preferred distance between random spawn points (relaxed on crowded maps). */
export const RANDOM_SPAWN_SPACING = 600;

// --- Unused in legacy, kept for Phase 5 options (DestroyableObject strengths, items) ---
export const SHIP_STRENGTH = 5;
export const ASTEROID_STRENGTH = 3;
/** Asteroid sizes (P11.6): full, half and quarter; radius, mass and hull scale with these. */
export const ASTEROID_SIZE_SCALE = [1, 0.65, 0.42] as const;
/** Sideways kick (units/tick) the two halves of a split asteroid fly apart with. */
export const ASTEROID_SPLIT_KICK = 1.2;
export const MISSILE_STRENGTH = 0.5;
export const FUEL_ITEM_LIFETIME_TICKS = 100;
export const MISSILE_ITEM_LIFETIME_TICKS = 100;

// --- Presentation values that are part of the legacy spec (used by view/audio layers) ---
/** Non-ship explosions are only audible within this distance of a ship. legacy: SoundManager.cpp */
export const EXPLOSION_SOUND_RADIUS = 500;
/** Camera distance model: visible height ≈ CAMERA_VIEW_FACTOR * (BASE + PER_SPEED * speed). */
export const CAMERA_BASE_DISTANCE = 700;
export const CAMERA_DISTANCE_PER_SPEED = 50;
/** 2 * tan(45° / 2): legacy gluPerspective fovy 45. */
export const CAMERA_VIEW_FACTOR = 2 * (Math.SQRT2 - 1); // 2·tan(π/8), exact form

// --- Modern rules (not in the original; tuning values chosen for play, see 02_ROADMAP Phase 5) ---
/** Fuel tank in ticks of thrust (9 s). Thrust burns 1 per tick. */
export const FUEL_MAX = 600;
export const FUEL_BURN_PER_TICK = 1;
/** Refill per tick while the thrust key is released (full tank in 36 s). */
export const FUEL_REGEN_PER_TICK = 0.25;
/** Homing missiles only see targets within this angle of their heading (60°). */
export const HOMING_SEEKER_HALF_ANGLE = Math.PI / 3;
/** Laser heat above this fraction of the maximum shows as "hot" in the HUD (overheat rule). */
export const HEAT_WARNING = 0.75;
/** Power-up pods (the original's FuelItem/MissileItem had radius 10 and were never spawned). */
export const PICKUP_RADIUS = 16;
/** Pods spawn at least this far from planet surfaces, ships, spawn points and other pods. */
export const PICKUP_PLANET_CLEARANCE = 150;
export const PICKUP_SHIP_CLEARANCE = 250;
export const PICKUP_SPAWN_POINT_CLEARANCE = 100;
export const PICKUP_PICKUP_CLEARANCE = 200;
/**
 * Safe asteroid spawns rule (I-027): a new asteroid must spawn at least this far from a ship
 * and from a spawn point (a ship may respawn there any moment).
 */
export const ASTEROID_SPAWN_SHIP_CLEARANCE = 400;
/** Safe asteroid spawns: ticks to wait before trying again when no spot was clear. */
export const ASTEROID_SPAWN_RETRY_TICKS = 15;
/** I-026: fueling stations — persistent map objects, never consumed, that refuel a lingering ship. */
export const FUEL_STATION_RADIUS = 24;
/** A ship within this distance of a station's centre receives its refill rate every tick. */
export const FUEL_STATION_RANGE = 100;
/** Hull damage per hit (hull-damage rule; hulls use the legacy strengths above). */
export const LASER_DAMAGE = 1;
export const MISSILE_DAMAGE = 3;
/** Ramming an asteroid costs the ship this much hull and destroys the asteroid. */
export const ASTEROID_RAM_DAMAGE = 2;

// --- Asteroid survival (single player; the original SinglePlayerPlayState concept) ---
/** Every this many ticks (30 s) the asteroid interval shrinks by SURVIVAL_INTERVAL_STEP. */
export const SURVIVAL_RAMP_TICKS = 2000;
export const SURVIVAL_INTERVAL_STEP = 20;
export const SURVIVAL_MIN_INTERVAL = 40;

/**
 * Version of the sim's behaviour, stored in replays (P10.6). Bump it whenever the same inputs
 * would play out differently (physics, rules, spawning, AI-free systems; the golden orbit and
 * determinism snapshots changing are the usual sign), so old replays are refused, not wrong.
 */
export const SIM_VERSION = 2;

// --- Objective modes (P11.2) ---
/** Ships per player in objective modes: effectively unlimited respawns. */
export const OBJECTIVE_SHIPS = 999_999;
/** King of the Hill: points to win, and ticks alone in the zone per point (~1 s). */
export const KOTH_POINTS_TO_WIN = 30;
export const KOTH_TICKS_PER_POINT = 67;
/** The hill is a band from planet surface + inner to surface + outer. */
export const KOTH_BAND_INNER = 100;
export const KOTH_BAND_OUTER = 550;
/** Capture the Flag: captures to win; flag and base sizes; a dropped flag goes home after this. */
export const CTF_CAPTURES_TO_WIN = 3;
export const CTF_FLAG_RADIUS = 24;
export const CTF_BASE_RADIUS = 90;
export const CTF_FLAG_RETURN_TICKS = 1333;
/** Respawn wait in objective modes (~3 s; legacy duels use RESPAWN_DELAY_TICKS). */
export const OBJECTIVE_RESPAWN_TICKS = 200;
/**
 * 2008 survival scoring (P11.5, req. 4.1.11/4.1.13): points for every second alive and for each
 * asteroid destroyed, 3 ships, a bonus ship at every multiple of SURVIVAL_2008_BONUS_EVERY.
 */
export const SURVIVAL_2008_SHIPS = 3;
export const SURVIVAL_2008_ASTEROID_POINTS = 25;
export const SURVIVAL_2008_TICKS_PER_POINT = 67;
export const SURVIVAL_2008_BONUS_EVERY = 300;
/** A flag carrier's thrust and top speed are scaled by this. */
export const CTF_CARRIER_FACTOR = 0.9;
